import { apiCall } from "@/common/api/ApiResult.model";
import {
  SourceConnection,
  ScanSession,
  SourceAcquisitionDescriptor,
  SourceBrief,
  DataSourceType,
  ScanUsageStatistics,
} from "./model";
import axios from "axios";
import { DATA_SOURCES_MAP } from "./data-relations/data-sources.map";
import { pluck, path, pipe2, gt } from "@/fp";
import { SharedState } from "@/common/state";

const motion_365 = {
  id: "motion_365",
  name: "Microsoft 365 Activities",
  type: "InMotion",
  processingType: "Continuous",
  multiConnectionAllowed: false,
  reentrantSessionAllowed: false,
  startParams: [],
  statistics: {
    connectionCount: 0,
    liveRunningScansCount: 0,
    lastScanAt: "0001-01-01T00:00:00",
    wasScanned: false,
  },
};

const g_suite = {
  id: "google_workspace" as DataSourceType,
  name: "Google Workspace",
  type: "InMotion",
  processingType: "Continuous",
  multiConnectionAllowed: false,
  reentrantSessionAllowed: false,
  startParams: [],
  statistics: {
    connectionCount: 0,
    liveRunningScansCount: 0,
    lastScanAt: "0001-01-01T00:00:00",
    wasScanned: false,
  },
};

const conn_count = pipe2(
  path<number>("statistics.connectionCount", () => 0),
  gt(0)
);
const local_source_map = pluck("icon", "desc", "name");
export const get_service_brief_list = apiCall<void, SourceBrief[]>(
  "data-sources.load-all-sources-status",
  async () => {
    const res = await axios.get("/findings/ds");
    const pre_set_services = [];
    switch (true) {
      case SharedState.in_motion_services_flags.value.is_google_org_only:
        pre_set_services.unshift(g_suite);
        break;
      case SharedState.in_motion_services_flags.value.is_ms_org_only:
        pre_set_services.unshift(motion_365);
        break;
      case SharedState.in_motion_services_flags.value.is_ms_and_google_org:
      default:
        pre_set_services.unshift(motion_365, g_suite);
        break;
    }

    return {
      success: true,
      value: [...pre_set_services, ...res.data]
        .filter((item: SourceBrief) => item.id in DATA_SOURCES_MAP)
        .map((item: SourceBrief) => {
          const local_item = local_source_map(DATA_SOURCES_MAP[item.id]);
          return {
            ...item,
            name: local_item.name,
            runtime: {
              ...local_item,
              enabled: item.type === "InMotion" ? true : conn_count(item),
            },
          } as SourceBrief;
        }),
    };
  }
);

export const get_source_acquisition_descriptor = apiCall<
  string,
  SourceAcquisitionDescriptor
>("data-source.get-source-acquisition-descriptor", async (source_id) => {
  const res = await axios.get(`/findings/ds/acquire/service/${source_id}`);
  return {
    success: true,
    value: res.data,
  };
});

//#region SOURCE CONNECTION SETUP

export const setup_data_source_via_keys = apiCall<
  {
    source_id: string;
    fields: Record<string, string>;
    connection_name: string;
  },
  boolean
>(
  "data-sources.setup-source-via-keys",
  async ({ fields, source_id, connection_name }) => {
    await axios.post(`/findings/ds/confirm/service/${source_id}`, {
      authParams: fields,
      name: connection_name,
    });
    return {
      success: true,
      value: true,
    };
  }
);

export const setup_source_via_token = apiCall<
  {
    source_id: string;
    consent: string;
    connection_name: string;
    auth_params?: Record<string, string>;
  },
  boolean
>(
  "data-sources.setup-source-via-token",
  async ({ consent, source_id, connection_name, auth_params }) => {
    const payload = {
      consent,
      authParams: auth_params,
      name: connection_name,
    };
    await axios.post(`/findings/ds/confirm/service/${source_id}`, payload);
    return {
      success: true,
      value: true,
    };
  }
);

//#endregion

//#region SOURCE CONNECTIONS

export const get_source_connections = apiCall<string, SourceConnection[]>(
  "data-sources.get-source-connections",
  async (source_id: string) => {
    const res = await axios.get(
      `/findings/ds/connections/service/${source_id}`
    );
    return {
      success: true,
      value: res.data.reverse(),
    };
  }
);

export const update_connection_name = apiCall<
  { source_id: string; connection_id: string; connection_name: string },
  boolean
>(
  "data-sources.update-connection",
  async ({ connection_id, connection_name, source_id }) => {
    await axios.patch(`/findings/ds/connections/service/${source_id}`, {
      id: connection_id,
      name: connection_name,
    });
    return {
      success: true,
      value: true,
    };
  }
);

/* 
Can u fix this from: https://qa-webapi.cognni.ai/findings/ds/delete/service/msft_365/connectionId/b5e094b5-7583-40db-aaa0-9a101daa9a1e

to https://qa-webapi.cognni.ai/findings/ds/connections/delete/service/msft_365/connectionId/b5e094b5-7583-40db-aaa0-9a101daa9a1e

*/

export const remove_connection = apiCall<
  { source_id: string; connection_id: string },
  boolean
>("data-sources.remove-connection", async ({ source_id, connection_id }) => {
  await axios.delete(
    `/findings/ds/connections/delete/service/${source_id}/connectionId/${connection_id}`
  );
  return {
    success: true,
    value: true,
  };
});

//#endregion

//#region SCAN SESSIONS

export const get_sessions_for_source = apiCall<DataSourceType, ScanSession[]>(
  "data-source.get-source-sessions",
  async (source_id) => {
    const res = await axios.get(`/findings/ds/sessions/service/${source_id}`);
    return {
      success: true,
      value: res.data,
    };
  }
);

export const get_sessions_usage_and_status = apiCall<
  { source_id: DataSourceType; sessions: string[] },
  ScanSession[]
>("data-source.get-source-sessions", async ({ source_id, sessions }) => {
  const res = await axios.post(`/findings/sessions/status`, {
    service: source_id,
    sessions,
  });
  return {
    success: true,
    value: res.data,
  };
});

export const start_scan_session = apiCall<
  {
    source_id: string;
    connection_id: string;
    session_name: string;
    start_params: Record<string, string>;
  },
  string
>(
  "data-sources.start-scan",
  async ({ source_id, connection_id, start_params, session_name }) => {
    const res = await axios.post(
      `/findings/ds/sessions/start/service/${source_id}/connection/${connection_id}`,
      {
        name: session_name,
        startParams: start_params,
      }
    );
    return {
      success: true,
      value: res.data.sessionId,
    };
  }
);

export const stop_scan_session = apiCall<
  { source_id: string; scan_session_id: string },
  boolean
>("data-sources.stop-scan", async ({ source_id, scan_session_id }) => {
  await axios.put(
    `/findings/ds/sessions/cancel/service/${source_id}/session/${scan_session_id}`,
    {
      reason: "client requested",
    }
  );
  return {
    success: true,
    value: true,
  };
});

//#endregion

export const usage_for_session_and_app = apiCall<
  { source_id: string; scan_session_id: string; app_id: string },
  ScanUsageStatistics
>("data-sources.stop-scan", async ({ source_id, scan_session_id, app_id }) => {
  const res = await axios.get(
    `/findings/usage/ds/${source_id}/app/${app_id}/session/${scan_session_id}`
  );
  return {
    success: true,
    value: res.data,
  };
});
